import { fetchCosmetics } from './cosmeticsApi';
import { getLocalApiOrigin } from '../localApi';
import { isWeb } from '../platform';

// Process-level cache of the cosmetics catalog (packs + the player's
// owned cosmetic card ids). Surfaces that need the catalog read this
// instead of each fetching independently. Short TTL so a fresh buy
// can invalidate and the next read sees the new ownership.
const TTL_MS = 2 * 60 * 1000;

let cached = null;
let cachedAt = 0;
let inFlight = null;
let currentUserId = null;
// Resolves once the most recent protected-mat registry PUT has reached
// the local server. Callers that depend on the registry being live
// (playmat preview/full warming) await this so their fetches don't
// race ahead of the PUT and 404 with `not-registered`.
let registryPushPromise = Promise.resolve();

// Set once the user's profile loads (typically from ArenaHub on
// mount). Triggers a registry push to the local server so the next
// /game-assets/protected-playmat/<id> request succeeds.
export function setCurrentUserId(userId) {
  const next = userId ? String(userId) : null;
  if (next === currentUserId) return;
  currentUserId = next;
  if (cached && currentUserId) {
    registryPushPromise = pushProtectedRegistry(cached.packs, currentUserId).catch(() => {});
  }
}

// Await the in-flight (or most recent) protected-registry PUT. Returns
// immediately if none is pending. See `registryPushPromise`.
export function awaitProtectedRegistry() {
  return registryPushPromise;
}

// PUT { userId, entries:[{cosmeticId, blobUrl}] } to the local
// server so the main process knows the user identity (for the
// watermark) and the Blob URL to fetch per cosmeticId. No prefetch
// here. Running heavy sharp + encrypt pipelines in the background
// during app startup competed with login / profile / sound loading
// and caused user-visible lag. First visit to the playmat sub-tab
// in a session takes ~1s per protected mat to warm the disk cache,
// then both the encrypted on-disk cache and the renderer's HTTP
// cache hold the bytes for the rest of the session (and across
// launches, since Chromium's disk cache persists).
async function pushProtectedRegistry(packs, userId) {
  // Web build has no local Electron server to push a registry to;
  // protected mats load their public CDN blobUrl directly instead.
  if (isWeb()) return;
  const entries = [];
  for (const p of packs || []) {
    if (p?.protected && p?.cosmeticId && p?.blobUrl) {
      entries.push({ cosmeticId: p.cosmeticId, blobUrl: p.blobUrl });
    }
  }
  if (entries.length === 0) return;
  try {
    await fetch(`${getLocalApiOrigin()}/game-assets/protected-playmat/_registry`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, entries }),
    });
  } catch (_) {}
}

export async function getCosmeticsCached() {
  const now = Date.now();
  if (cached && now - cachedAt < TTL_MS) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const data = await fetchCosmetics();
      cached = {
        packs: Array.isArray(data?.packs) ? data.packs : [],
        // Universal tokens are shipped by the server alongside the
        // paid catalog. They appear in every player's Token Browser
        // regardless of ownership. Old servers (pre-universal-token
        // deploys) omit the field. Fall back to an empty array so
        // the client renders cleanly against either version.
        universalTokens: Array.isArray(data?.universalTokens) ? data.universalTokens : [],
        owned: Array.isArray(data?.owned) ? data.owned : [],
      };
      cachedAt = Date.now();
      if (currentUserId) {
        registryPushPromise = pushProtectedRegistry(cached.packs, currentUserId).catch(() => {});
      }
      return cached;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function invalidateCosmeticsCache() {
  cached = null;
  cachedAt = 0;
}

// Synchronous peek at the cached data. Returns null if nothing's
// been fetched yet. Callers needing freshness should use
// `getCosmeticsCached()` instead. Useful for boot-time paths that
// don't want to await but can correct themselves once the cache
// fills in (e.g. fallback to a free playmat if the saved one is
// locked).
export function peekCosmeticsCached() {
  return cached;
}

// Web build: resolve a protected mat's public CDN URL from the cached
// catalog (the server ships `blobUrl` per protected pack). Sync peek;
// returns null until the catalog has been fetched once.
export function peekProtectedBlobUrl(cosmeticId) {
  if (!cosmeticId || !cached) return null;
  const pack = cached.packs.find((p) => p?.protected && p?.cosmeticId === cosmeticId);
  return pack?.blobUrl || null;
}

// Web build: derive a pre-baked downscaled variant URL from a protected
// mat's master blobUrl. scripts/upload-playmats-to-r2.js publishes
// `previews/` (~480px), `medium/` (~1100px) and `full/` (3500px table
// mat) variants next to each master, mirroring the downscales native's
// local server produces at serve time. Never hand the 68 MP master to
// an <img> tile or the table: decoding a handful of those at once froze
// the renderer for seconds.
const MAT_VARIANT_DIRS = { medium: 'medium', full: 'full' };
export function protectedMatVariantUrl(blobUrl, variant) {
  if (!blobUrl) return null;
  const i = blobUrl.lastIndexOf('/');
  if (i === -1) return blobUrl;
  const dir = MAT_VARIANT_DIRS[variant] || 'previews';
  return `${blobUrl.slice(0, i)}/${dir}/${blobUrl.slice(i + 1)}`;
}

// Patch the cached `owned` array after a successful pack buy so the
// next read sees the new entitlement without a round-trip.
export function noteCosmeticUnlocked(cardId) {
  if (!cached || !cardId) return;
  if (!cached.owned.includes(cardId)) {
    cached.owned = [...cached.owned, cardId];
  }
}
